// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import React, { useState, useMemo, useCallback } from 'react';
import { Wizard } from '@cloudscape-design/components';
import { useHistory } from 'react-router-dom';
import Breadcrumbs from '../../Utils/Breadcrumbs';
import { HOMEPAGE_PATH, ACCOUNTS } from '../../routes';
import MethodSelector from './MethodSelector';
import PersistentSubSelector from './PersistentSubSelector';
import CliInstallStep from './steps/CliInstallStep';
import CliOnboardStep from './steps/CliOnboardStep';
import CdkInstallStep from './steps/CdkInstallStep';
import CdkDeployStep from './steps/CdkDeployStep';
import CfDownloadStep from './steps/CfDownloadStep';
import CfGlobalDeployStep from './steps/CfGlobalDeployStep';
import CfRegionalDeployStep from './steps/CfRegionalDeployStep';
import VerificationStep from './VerificationStep';

/**
 * Computes the wizard step array based on the current selection state.
 * This is a pure function: same inputs always produce the same step structure.
 */
export const computeSteps = (
  selectedMethod,
  selectedPersistentMethod,
  { onSelectMethod, onSelectPersistentMethod }
) => {
  const methodStep = {
    title: 'Choose your method',
    content: (
      <MethodSelector
        selectedMethod={selectedMethod}
        onSelect={onSelectMethod}
      />
    ),
  };

  if (selectedMethod === 'cli') {
    return [
      methodStep,
      { title: 'Install CLI tool', content: <CliInstallStep /> },
      { title: 'Run onboard command', content: <CliOnboardStep /> },
      {
        title: 'Verify onboarding',
        content: <VerificationStep method="cli" />,
      },
    ];
  }

  if (selectedMethod === 'persistent') {
    const subSelectorStep = {
      title: 'Choose infrastructure tool',
      content: (
        <PersistentSubSelector
          selectedPersistentMethod={selectedPersistentMethod}
          onSelect={onSelectPersistentMethod}
        />
      ),
    };

    if (selectedPersistentMethod === 'cdk') {
      return [
        methodStep,
        subSelectorStep,
        { title: 'Install CDK constructs', content: <CdkInstallStep /> },
        { title: 'Deploy stack', content: <CdkDeployStep /> },
        {
          title: 'Verify onboarding',
          content: <VerificationStep method="cdk" />,
        },
      ];
    }

    if (selectedPersistentMethod === 'cloudformation') {
      return [
        methodStep,
        subSelectorStep,
        { title: 'Download templates', content: <CfDownloadStep /> },
        { title: 'Deploy global resources', content: <CfGlobalDeployStep /> },
        { title: 'Deploy regional resources', content: <CfRegionalDeployStep /> },
        {
          title: 'Verify onboarding',
          content: (
            <VerificationStep method="cloudformation" />
          ),
        },
      ];
    }

    // Persistent selected but no sub-method yet
    return [methodStep, subSelectorStep];
  }

  // Nothing selected yet
  return [methodStep];
};

const OnboardingGuidePage = () => {
  const history = useHistory();

  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [selectedPersistentMethod, setSelectedPersistentMethod] = useState(null);

  const onSelectMethod = useCallback((method) => {
    setSelectedMethod(method);
    setSelectedPersistentMethod(null);
    setActiveStepIndex(0);
  }, []);

  const onSelectPersistentMethod = useCallback((method) => {
    setSelectedPersistentMethod(method);
    setActiveStepIndex(1);
  }, []);

  const steps = useMemo(
    () =>
      computeSteps(selectedMethod, selectedPersistentMethod, {
        onSelectMethod,
        onSelectPersistentMethod,
      }),
    [selectedMethod, selectedPersistentMethod, onSelectMethod, onSelectPersistentMethod]
  );

  const onNavigate = useCallback(
    ({ detail: { requestedStepIndex } }) => {
      // Guard: cannot advance past step 0 if no method selected
      if (selectedMethod === null && requestedStepIndex > 0) {
        return;
      }

      // Guard: cannot advance past the sub-selector step (index 1) if persistent
      // is selected but no sub-method chosen yet
      if (
        selectedMethod === 'persistent' &&
        selectedPersistentMethod === null &&
        requestedStepIndex > 1
      ) {
        return;
      }

      setActiveStepIndex(requestedStepIndex);
    },
    [selectedMethod, selectedPersistentMethod]
  );

  const onCancel = useCallback(() => {
    history.push(HOMEPAGE_PATH);
  }, [history]);

  const onSubmit = useCallback(() => {
    history.push(ACCOUNTS);
  }, [history]);

  const breadcrumbItems = [
    { text: 'Onboarding Guide', href: '/onboarding-guide' },
  ];

  return (
    <>
      <Breadcrumbs items={breadcrumbItems} />
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <Wizard
          i18nStrings={{
            stepNumberLabel: (stepNumber) => `Step ${stepNumber}`,
            collapsedStepsLabel: (stepNumber, stepsCount) =>
              `Step ${stepNumber} of ${stepsCount}`,
            cancelButton: 'Cancel',
            previousButton: 'Previous',
            nextButton: 'Next',
            submitButton: 'Go to accounts',
            optional: 'optional',
          }}
          activeStepIndex={activeStepIndex}
          onNavigate={onNavigate}
          onCancel={onCancel}
          onSubmit={onSubmit}
          steps={steps}
        />
      </div>
    </>
  );
};

export default OnboardingGuidePage;
